import {
  AddressElement,
  CardElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import type { StripeAddressElementChangeEvent } from "@stripe/stripe-js";
import { useAuth } from "@via/hooks/useAuth";
import { useState } from "react";
import StripeBadge from "@via/pages/billing/stripe-badge.tsx";
import { Spinner } from "@via/components/loading/loading.tsx";
import { classNames } from "@via/utils/helpers.tsx";
import {
  BannerType,
  MessageBanner,
} from "@via/components/message-banner/message-banner";
import { useCheckSubscriptionHealth } from "@via/pages/billing/billing.api.tsx";
import { OnboardingStep } from "common";

interface CardSetupProps {
  email: string;
  clientSecret: string;
  onCardConfirmed: (paymentMethodId: string) => void;
  isUpgrade?: boolean;
  voucherCode?: {
    enabled: boolean;
    onToggle: (checked: boolean) => void;
    value: string;
    onChange: (value: string) => void;
    onRedeem: () => void;
    isRedeeming: boolean;
  };
}

const CardSetup: React.FC<CardSetupProps> = ({
  email,
  clientSecret,
  onCardConfirmed,
  isUpgrade,
  voucherCode,
}) => {
  const { user } = useAuth();
  const stripe = useStripe();
  const elements = useElements();

  const { data: checkSubscriptionHealth } = useCheckSubscriptionHealth({
    enabled: user?.onboardingStep === OnboardingStep.OrganizationCreated,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [addressComplete, setAddressComplete] = useState(false);
  const [addressValue, setAddressValue] = useState<
    StripeAddressElementChangeEvent["value"] | null
  >(null);

  const [cardError, setCardError] = useState<string | null>(null);
  const [cardComplete, setCardComplete] = useState(false);

  const [addressError, setAddressError] = useState<string | null>(null);

  const isPaymentDisabled = !!voucherCode?.enabled;

  const handleAccessCodeToggle = (checked: boolean) => {
    voucherCode?.onToggle(checked);
    if (checked) {
      elements?.getElement(CardElement)?.clear();
      setCardComplete(false);
      setCardError(null);
      setAddressComplete(false);
      setAddressValue(null);
      setAddressError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (voucherCode?.enabled) {
      if (!voucherCode.value.trim()) {
        return;
      }
      voucherCode.onRedeem();
      return;
    }

    setCardError(null);
    setAddressError(null);

    if (!stripe || !elements || !clientSecret) return;

    const cardElement = elements.getElement(CardElement);
    const addressElement = elements.getElement(AddressElement);

    if (!cardElement || !addressElement) {
      return;
    }

    let hasError = false;

    if (!addressComplete) {
      setAddressError("Please complete your billing address.");
      hasError = true;
    }

    if (!cardComplete) {
      setCardError("Please complete your card details.");
      hasError = true;
    }

    if (hasError) return;

    setIsSubmitting(true);
    const { setupIntent, error: confirmError } = await stripe.confirmCardSetup(
      clientSecret,
      {
        payment_method: {
          card: cardElement,
          billing_details: {
            email,
            address: addressValue?.address,
            name: addressValue?.name || "",
            phone: addressValue?.phone || undefined,
          },
        },
      }
    );

    if (confirmError || !setupIntent) {
      setCardError(confirmError?.message || "Something went wrong.");
      setIsSubmitting(false);
      return;
    }

    onCardConfirmed(setupIntent.payment_method as string);
    setIsSubmitting(false);
  };
  return (
    <form onSubmit={handleSubmit} className="max-w-md mx-auto space-y-4">
      {checkSubscriptionHealth &&
        user?.onboardingStep === OnboardingStep.OrganizationCreated &&
        !isUpgrade && (
          <div className="mt--15">
            <MessageBanner
              messages={checkSubscriptionHealth.message}
              title="Inactive Organization"
              type={BannerType.Error}
            />
          </div>
        )}
      <div className="text-2xl text-center font-bold mb-10">
        Enter your credit card information
      </div>

      <div
        className={classNames(
          "space-y-4 transition-opacity duration-200",
          isPaymentDisabled ? "opacity-50 pointer-events-none select-none" : ""
        )}
        aria-disabled={isPaymentDisabled || undefined}>
        {/* Address */}
        <div>
          <label className="text-sm font-medium text-gray-700">
            Billing Address
          </label>
          <div
            className={classNames(
              "mt-1 mb-4 rounded-md border px-3 py-3 shadow-sm",
              addressError
                ? "border-red-500 bg-red-50"
                : "border-gray-300 bg-white"
            )}>
            <AddressElement
              options={{
                mode: "billing",
                fields: {
                  phone: "always",
                },
              }}
              onChange={(event: StripeAddressElementChangeEvent) => {
                if (isPaymentDisabled) return;
                setAddressComplete(event.complete);
                setAddressValue(event?.value || null);
                setAddressError(null);
              }}
            />
          </div>
          {addressError && (
            <div className="text-red-600 text-sm mt-1">{addressError}</div>
          )}
        </div>

        {/* Card */}
        <div>
          <label className="text-sm font-medium text-gray-700">Card Info</label>
          <div
            className={classNames(
              "mt-1 rounded-md px-3 py-3 shadow-sm",
              cardError
                ? "border border-red-500 bg-red-50"
                : "border border-gray-300 bg-white focus-within:ring-1 focus-within:ring-indigo-500 focus-within:border-indigo-500"
            )}>
            <CardElement
              options={{
                hidePostalCode: true,
                disabled: isPaymentDisabled,
                style: {
                  base: {
                    fontSize: "16px",
                    color: "#111827",
                    fontFamily: "inherit",
                    "::placeholder": { color: "#9CA3AF" },
                  },
                  invalid: {
                    color: "#EF4444",
                  },
                },
                iconStyle: "solid",
              }}
              onChange={event => {
                setCardComplete(event.complete);
                setCardError(event.error?.message || null);
              }}
            />
          </div>
        </div>

        {cardError && (
          <div className="text-red-600 text-sm mt-1 transition-opacity duration-150">
            {cardError}
          </div>
        )}

        <StripeBadge />
      </div>

      {voucherCode && (
        <div className="border-t border-gray-200 pt-4 mt-4">
          <label className="inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={voucherCode.enabled}
              onChange={e => handleAccessCodeToggle(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span className="ml-2 text-sm text-gray-500">
              I have an access code
            </span>
          </label>

          {voucherCode.enabled && (
            <input
              type="text"
              value={voucherCode.value}
              onChange={e => voucherCode.onChange(e.target.value)}
              placeholder="Enter code"
              className="mt-2 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              disabled={voucherCode.isRedeeming}
            />
          )}
        </div>
      )}

      <div className="flex justify-center gap-4 py-4">
        <button
          type="submit"
          disabled={!stripe || isSubmitting || voucherCode?.isRedeeming}
          className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-500 disabled:opacity-50">
          <Spinner loading={isSubmitting || !!voucherCode?.isRedeeming}>
            Next
          </Spinner>
        </button>
      </div>
    </form>
  );
};

export default CardSetup;
